import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Toast from '../common/Toast';
import AuthContext from '../../context/AuthContext';
import { getDoctorFilterParams } from '../../utils/doctorFilter';
import { 
  Card, 
  Button, 
  Table, 
  FormControl, 
  InputGroup, 
  Spinner,
  Pagination,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Badge
} from 'react-bootstrap';
import { FaSearch, FaPlus, FaFileMedical, FaTrash, FaUserPlus, FaExclamationTriangle, FaArchive, FaKey, FaFileInvoiceDollar } from 'react-icons/fa';
import './Patients.css';

const Patients = () => {
  const { user } = useContext(AuthContext);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [patientToQueue, setPatientToQueue] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [queueError, setQueueError] = useState(null);
  const [queueFormData, setQueueFormData] = useState({
    doctorId: '',
    notes: '',
    isEmergency: false
  });
  const [queueDoctors, setQueueDoctors] = useState([]);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [patientToArchive, setPatientToArchive] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [patientForLogin, setPatientForLogin] = useState(null);
  const [loginFormData, setLoginFormData] = useState({ password: '', confirmPassword: '' });
  const [showPasswordResult, setShowPasswordResult] = useState(false);
  const [savedPassword, setSavedPassword] = useState('');

  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const filterParams = getDoctorFilterParams(user);
        const res = await axios.get('/api/patients.php', { params: filterParams });
        setPatients(res.data);
        setFilteredPatients(res.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchPatients();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = patients.filter(
        patient =>
          patient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          patient.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (patient.contactNumber && patient.contactNumber.includes(searchQuery))
      );
      setFilteredPatients(filtered);
      setCurrentPage(1);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchQuery, patients]);

  // Handle add to queue
  const handleAddToQueueClick = async (patient) => {
    setPatientToQueue(patient);
    setQueueError(null);
    
    // Reset form data
    setQueueFormData({
      doctorId: '',
      notes: '',
      isEmergency: false
    });
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Get today's paid bills for this patient
      const billsRes = await axios.get(`/api/billing?patientId=${patient.id}`);
      const todayPaidBills = billsRes.data.filter(bill => {
        const billDate = new Date(bill.createdAt).toISOString().split('T')[0];
        return billDate === today && bill.paymentStatus === 'complete' && bill.doctorId;
      });
      
      if (todayPaidBills.length === 0) {
        setToastMessage('Payment required! Please complete payment with a doctor before adding to queue.');
        setToastType('error');
        setShowToast(true);
        return;
      }
      
      // Get today's queue entries for this patient
      const queueRes = await axios.get(`/api/queue?date=${today}`);
      const todayQueueEntries = queueRes.data.filter(q => q.patientId === patient.id);
      
      // Count payments per doctor
      const paymentsByDoctor = {};
      todayPaidBills.forEach(bill => {
        paymentsByDoctor[bill.doctorId] = (paymentsByDoctor[bill.doctorId] || 0) + 1;
      });
      
      // Count queue entries per doctor
      const queueByDoctor = {};
      todayQueueEntries.forEach(q => {
        queueByDoctor[q.doctorId] = (queueByDoctor[q.doctorId] || 0) + 1;
      });
      
      // Find doctors with available queue slots (payments > queue entries)
      const availableDoctorIds = Object.keys(paymentsByDoctor).filter(doctorId => {
        const payments = paymentsByDoctor[doctorId];
        const queueEntries = queueByDoctor[doctorId] || 0;
        return payments > queueEntries;
      }).map(id => parseInt(id));
      
      if (availableDoctorIds.length === 0) {
        setToastMessage('No queue slots available. Please make another payment to add to queue again.');
        setToastType('error');
        setShowToast(true);
        return;
      }
      
      // Fetch all active doctors
      const doctorsRes = await axios.get('/api/doctors.php?active=1');
      
      // Filter doctors who have available queue slots
      const availableDoctors = doctorsRes.data.filter(doc => availableDoctorIds.includes(doc.id));
      
      if (availableDoctors.length > 0) {
        setQueueDoctors(availableDoctors);
        setQueueFormData({
          doctorId: availableDoctors[0].id.toString(),
          notes: '',
          isEmergency: false
        });
        setShowQueueModal(true);
      } else {
        setToastMessage('No doctors available. Please complete payment with a doctor first.');
        setToastType('error');
        setShowToast(true);
      }
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setToastMessage('Failed to load doctors. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };
  
  // Handle queue form input changes
  const handleQueueFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQueueFormData({
      ...queueFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Submit add to queue
  const handleSubmitQueue = async (e) => {
    e.preventDefault();
    
    if (!patientToQueue) {
      setQueueError('Patient not selected.');
      return;
    }
    
    if (!queueFormData.doctorId) {
      setQueueError('Please select a doctor.');
      return;
    }
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Check if patient has appointment today with selected doctor (not completed)
      const appointmentsRes = await axios.get(`/api/appointments?patientId=${patientToQueue.id}&doctorId=${queueFormData.doctorId}&date=${today}`);
      const todayAppointment = appointmentsRes.data.find(app => 
        ['scheduled', 'confirmed'].includes(app.status)
      );
      
      const queueData = {
        patientId: parseInt(patientToQueue.id),
        doctorId: parseInt(queueFormData.doctorId),
        appointmentId: todayAppointment ? todayAppointment.id : null,
        notes: queueFormData.notes,
        isEmergency: queueFormData.isEmergency
      };
      
      await axios.post('/api/queue.php', queueData);
      
      // Close modal and show success
      setShowQueueModal(false);
      
      setToastMessage(`${patientToQueue.name} added to queue successfully!`);
      setToastType('success');
      setShowToast(true);
      
      // Refresh patients list to update queue status
      const res = await axios.get('/api/patients.php');
      setPatients(res.data);
      setFilteredPatients(res.data);
    } catch (err) {
      console.error('Error adding to queue:', err);
      setQueueError(err.response?.data?.msg || 'Failed to add patient to queue. Please try again.');
    }
  };

  // Handle delete patient
  const handleDeleteClick = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!patientToDelete) return;
    
    try {
      // Use id instead of _id for SQLite
      await axios.delete(`/api/patients.php?id=${patientToDelete.id}`);
      
      // Remove patient from state using id instead of _id
      setPatients(patients.filter(p => p.id !== patientToDelete.id));
      setShowDeleteModal(false);
      setPatientToDelete(null);
      
      setToastMessage('Patient deleted successfully!');
      setToastType('delete');
      setShowToast(true);
    } catch (err) {
      console.error(err);
      // Handle error (could add error state and display message)
    }
  };

  // Handle archive patient
  const handleArchiveClick = (patient) => {
    setPatientToArchive(patient);
    setShowArchiveModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!patientToArchive) return;
    
    try {
      await axios.put(`/api/patients.php?id=${patientToArchive.id}&action=archive`);
      
      // Remove patient from state
      setPatients(patients.filter(p => p.id !== patientToArchive.id));
      setShowArchiveModal(false);
      setPatientToArchive(null);
      
      setToastMessage('Patient archived successfully!');
      setToastType('info');
      setShowToast(true);
    } catch (err) {
      console.error(err);
      // Handle error (could add error state and display message)
    }
  };

  // Handle close login modal
  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
    setShowPasswordResult(false);
    setLoginFormData({ password: '', confirmPassword: '' });
    setSavedPassword('');
    setPatientForLogin(null);
  };

  // Handle enable login
  const handleEnableLoginClick = (patient) => {
    setPatientForLogin(patient);
    setLoginFormData({ password: '', confirmPassword: '' });
    setShowPasswordResult(false);
    setSavedPassword('');
    setShowLoginModal(true);
  };

  const handleLoginFormChange = (e) => {
    setLoginFormData({ ...loginFormData, [e.target.name]: e.target.value });
  };

  const handleSubmitLogin = async (e) => {
    e.preventDefault();
    
    if (loginFormData.password !== loginFormData.confirmPassword) {
      setToastMessage('Passwords do not match!');
      setToastType('error');
      setShowToast(true);
      return;
    }
    
    if (loginFormData.password.length < 4) {
      setToastMessage('Password must be at least 4 characters!');
      setToastType('error');
      setShowToast(true);
      return;
    }
    
    try {
      await axios.put(`/api/patients.php?id=${patientForLogin.id}&action=enable-login`, {
        password: loginFormData.password
      });
      
      // Update patient in state
      setPatients(patients.map(p => 
        p.id === patientForLogin.id ? { ...p, loginEnabled: true } : p
      ));
      
      // Show password result
      setSavedPassword(loginFormData.password);
      setShowPasswordResult(true);
    } catch (err) {
      console.error(err);
      setToastMessage('Failed to enable login. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Pagination
  const indexOfLastPatient = currentPage * patientsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
  const currentPatients = filteredPatients.slice(indexOfFirstPatient, indexOfLastPatient);

  const paginate = pageNumber => setCurrentPage(pageNumber);

  // Get page numbers
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredPatients.length / patientsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="patients-container">
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage} 
        type={toastType}
      />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Patients</h2>
        <div className="patients-header-actions">
          {hasPermission('viewArchive') && (
            <Link to="/patients/archive" className="btn btn-secondary me-2">
              <FaArchive className="me-2" /> View Archive
            </Link>
          )}
          {hasPermission('addPatient') && (
            <Link to="/patients/add">
              <Button variant="primary">
                <FaPlus className="me-2" /> Add New Patient
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Card className="mb-4">
        <Card.Header>
          <InputGroup className="search-input-group">
            <FormControl
              placeholder="Search by name, ID or contact number"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <Button variant="outline-secondary">
              <FaSearch />
            </Button>
          </InputGroup>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredPatients.length > 0 ? (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Patient ID</th>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Contact</th>
                    <th>Queue Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPatients.map(patient => (
                    <tr key={patient.id}>
                      <td className="patient-id">{patient.patientId}</td>
                      <td>{patient.name}</td>
                      <td>{patient.age}</td>
                      <td>{patient.gender}</td>
                      <td>{patient.contactNumber}</td>
                      <td>
                        {patient.queueStatus === 'available' && <Badge bg="success">Queue Available</Badge>}
                        {patient.queueStatus === 'waiting' && <Badge bg="warning">In Queue</Badge>}
                        {patient.queueStatus === 'in-progress' && <Badge bg="primary">With Doctor</Badge>}
                        {!patient.queueStatus && <span className="text-muted">-</span>}
                      </td>
                      <td className="table-actions" style={{whiteSpace: 'nowrap'}}>
                        {hasPermission('viewPatient') && (
                          <Link to={`/patients/${patient.id}`} className="btn btn-sm btn-info me-2">
                            View
                          </Link>
                        )}
                        {hasPermission('newReport') && (
                          <Link to={`/reports/create/${patient.id}`} className="btn btn-sm btn-success me-2">
                            <FaFileMedical className="me-1" /> New Report
                          </Link>
                        )}
                        {hasPermission('archivePatient') && (
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleArchiveClick(patient)}
                            title="Archive Patient"
                          >
                            <FaArchive />
                          </Button>
                        )}
                        {hasPermission('enableLogin') && (
                          <Button 
                            variant={patient.loginEnabled ? 'success' : 'warning'}
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEnableLoginClick(patient)}
                            title={patient.loginEnabled ? 'Login Enabled' : 'Enable Patient Login'}
                          >
                            <FaKey />
                          </Button>
                        )}
                        {hasPermission('deletePatient') && (
                          <Button 
                            variant="danger" 
                            size="sm"
                            onClick={() => handleDeleteClick(patient)}
                            title="Delete Patient"
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Pagination className="justify-content-center">
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                />
                
                {pageNumbers.map(number => (
                  <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, pageNumbers.length))
                  }
                  disabled={currentPage === pageNumbers.length}
                />
              </Pagination>
            </>
          ) : (
            <div className="text-center my-5">
              <p>No patients found. Please add a new patient.</p>
              <Link to="/patients/add">
                <Button variant="primary">
                  <FaPlus className="me-2" /> Add New Patient
                </Button>
              </Link>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the patient <strong>{patientToDelete?.name}</strong>?
          <p className="mt-2 text-danger">
            <strong>Warning:</strong> This action cannot be undone and will remove all patient data 
            including ALL reports associated with this patient.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete Patient and Reports
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)}>
        <Modal.Header>
          <Modal.Title>Archive Patient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to archive the patient <strong>{patientToArchive?.name}</strong>?
          <p className="mt-2">
            Archived patients will be hidden from the main list but can be accessed through the archive section.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmArchive}>
            Archive Patient
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add to Queue Modal */}
      <Modal show={showQueueModal} onHide={() => setShowQueueModal(false)}>
        <Modal.Header>
          <Modal.Title>Add Patient to Queue</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitQueue}>
          <Modal.Body>
            {queueError && (
              <Alert variant="danger" className="mb-3">
                {queueError}
              </Alert>
            )}

            <div className="mb-3">
              <strong>Patient:</strong> {patientToQueue?.name}{' '}
              <span className="text-muted">({patientToQueue?.age} yrs, {patientToQueue?.gender})</span>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Select Doctor *</Form.Label>
              <Form.Select
                name="doctorId"
                value={queueFormData.doctorId}
                onChange={handleQueueFormChange}
                required
              >
                <option value="">-- Select Doctor --</option>
                {queueDoctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    Dr. {doctor.name} - {doctor.specialization}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={queueFormData.notes}
                onChange={handleQueueFormChange}
                placeholder="Add any notes about this patient's visit"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="emergency"
                name="isEmergency"
                label={
                  <>
                    <FaExclamationTriangle className="text-danger me-1" /> 
                    Mark as Emergency (will be prioritized in queue)
                  </>
                }
                checked={queueFormData.isEmergency}
                onChange={handleQueueFormChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQueueModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add to Queue
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Enable Login Modal */}
      <Modal show={showLoginModal} onHide={handleCloseLoginModal}>
        <Modal.Header>
          <Modal.Title>
            {patientForLogin?.loginEnabled ? 'Update' : 'Enable'} Patient Login
          </Modal.Title>
        </Modal.Header>
        {!showPasswordResult ? (
          <Form onSubmit={handleSubmitLogin}>
            <Modal.Body>
              <div className="mb-3">
                <strong>Patient:</strong> {patientForLogin?.name}
                <br />
                <strong>Patient ID:</strong> {patientForLogin?.patientId}
              </div>

              {patientForLogin?.loginEnabled && (
                <Alert variant="info">
                  Login is already enabled for this patient. You can update the password below.
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>New Password *</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={loginFormData.password}
                  onChange={handleLoginFormChange}
                  placeholder="Enter password (min 4 characters)"
                  required
                  minLength={4}
                />
                <Form.Text className="text-muted">
                  Patient will use their Patient ID and this password to login
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm Password *</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={loginFormData.confirmPassword}
                  onChange={handleLoginFormChange}
                  placeholder="Confirm password"
                  required
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseLoginModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {patientForLogin?.loginEnabled ? 'Update Password' : 'Enable Login'}
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <>
            <Modal.Body>
              <Alert variant="success">
                <h5>✓ Login Credentials Set Successfully!</h5>
                <hr />
                <Row>
                  <Col md={4}><strong>Patient Name:</strong></Col>
                  <Col md={8}>{patientForLogin?.name}</Col>
                </Row>
                <Row className="mt-2">
                  <Col md={4}><strong>Patient ID:</strong></Col>
                  <Col md={8}><code>{patientForLogin?.patientId}</code></Col>
                </Row>
                <Row className="mt-2">
                  <Col md={4}><strong>Password:</strong></Col>
                  <Col md={8}>
                    <code style={{fontSize: '1.1em', background: '#fff', padding: '5px 10px', borderRadius: '4px'}}>
                      {savedPassword}
                    </code>
                  </Col>
                </Row>
                <hr />
                <small className="text-danger">
                  ⚠️ <strong>Important:</strong> Save these credentials now. Password cannot be retrieved later.
                </small>
              </Alert>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="success" onClick={() => {
                handleCloseLoginModal();
                setToastMessage(`Login enabled for ${patientForLogin.name}!`);
                setToastType('success');
                setShowToast(true);
              }}>
                Done
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Patients; 

